import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import AlefBetStaff from "@/components/AlefBetStaff";
import JmlLink from "@/components/JmlLink";
import SentenceLines from "@/components/SentenceLines";
import { displayDate, artworkUrl } from "@/pages/PodcastEpisode";
import { PODCAST_PLATFORMS as PLATFORMS } from "@/lib/links";
import { logDebug } from "@/lib/log";

const EASE = [0.4, 0, 0.2, 1];

/**
 * Episode archive. Built for 100+ entries from day one: every episode is a
 * record here, filtered client-side by Parsha, and each one gets its own page
 * at /podcast/:slug once there's something to put on it.
 *
 * Shape: { number, slug, parsha, parshaHe, date, blurb, videoId }
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.8, ease: EASE },
};

const goldCta =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-7 h-12 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12]";
const goldStyle = {
  backgroundColor: "transparent",
  color: "#F2D589",
  border: "1px solid #F2D589",
  letterSpacing: "0.18em",
  fontWeight: 500,
};
const plainCta =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-7 h-12 text-xs uppercase transition-colors hover:border-[#FCA07A] hover:text-[#FFD9C2]";
const plainStyle = {
  border: "1px solid #241246",
  color: "#C6BBA9",
  letterSpacing: "0.18em",
  fontWeight: 500,
};

export default function Podcast() {
  const [query, setQuery] = useState("");
  const [EPISODES, setEpisodes] = useState([]);
  const [newPatrons, setNewPatrons] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/patrons/new`)
      .then(({ data }) => setNewPatrons(data.patrons || []))
      .catch((err) => logDebug("new patrons unavailable", err));
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/podcast/episodes`)
      .then(({ data }) =>
        setEpisodes(
          (data.episodes || []).map((e) => ({
            number: e.number,
            slug: e.slug,
            parsha: e.parsha,
            parshaHe: e.parsha_he,
            date: displayDate(e),
            blurb: e.blurb,
            videoId: e.video_id,
            art: artworkUrl(e),
          }))
        )
      )
      .catch((err) => logDebug("episodes unavailable", err));
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return EPISODES;
    return EPISODES.filter(
      (e) =>
        e.parsha.toLowerCase().includes(q) ||
        (e.parshaHe || "").includes(q) ||
        String(e.number) === q
    );
  }, [query, EPISODES]);

  return (
    <MarketingShell active="/podcast">
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        data-testid="podcast-hero"
      >
        <p className="text-[11px] uppercase mb-4" style={{ color: "#F2D589", letterSpacing: "0.28em" }}>
          The Podcast
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "var(--step-3)",
            letterSpacing: "-0.02em",
            lineHeight: 1.12,
            maxWidth: "26ch",
          }}
          data-testid="podcast-headline"
        >
          <SentenceLines text="The Alephonics Podcast. One phrase a week, played." />
        </h1>
        <p className="mt-8 max-w-[68ch] text-base sm:text-lg" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
          Every week the name of the Parsha goes through the mapping method, and then it gets played.
          Video first, so you can watch the letters become the notes.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="podcast-platforms">
          {PLATFORMS.map((pf) =>
            pf.href ? (
              <a
                key={pf.label}
                href={pf.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`podcast-sub-${pf.label.toLowerCase()}`}
                className={plainCta}
                style={plainStyle}
              >
                {pf.label}
              </a>
            ) : (
              <span
                key={pf.label}
                data-testid={`podcast-sub-${pf.label.toLowerCase()}`}
                className={plainCta}
                style={{ ...plainStyle, opacity: 0.45 }}
                title="Live with episode one"
              >
                {pf.label}
              </span>
            )
          )}
        </div>
      </motion.section>

      {/* ── the 22-note demo ── */}
      <motion.section {...reveal} className="mt-24" data-testid="podcast-alefbet">
        <AlefBetStaff />
      </motion.section>

      {/* ── latest episode ── */}
      <motion.section {...reveal} className="mt-24" data-testid="podcast-latest">
        <p className="text-[11px] uppercase" style={{ color: "#F2D589", letterSpacing: "0.24em" }}>
          Latest episode
        </p>
        {EPISODES.length === 0 ? (
          <div
            className="mt-8 px-8 py-14 sm:px-12"
            style={{ border: "1px solid #241246", backgroundColor: "#0C0826" }}
            data-testid="podcast-empty"
          >
            <h2
              className="max-w-[30ch]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
            >
              <SentenceLines text="Episode one is on its way." />
            </h2>
            <p className="mt-6 max-w-[62ch] text-base" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
              The archive starts here. Subscribe on any platform above and it&apos;ll find you, or put
              a word through the tool in the meantime and hear the method for yourself.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <JmlLink className={goldCta} style={goldStyle} testId="podcast-empty-tryit">
                Hear a word or verse
              </JmlLink>
              <Link to="/patrons" className={plainCta} style={plainStyle} data-testid="podcast-empty-patrons">
                Support the music
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-8" data-testid="podcast-latest-episode">
            <div className="w-full" style={{ aspectRatio: "16 / 9", backgroundColor: "#0C0826" }}>
              <iframe
                title={`Episode ${EPISODES[0].number} — ${EPISODES[0].parsha}`}
                src={`https://www.youtube.com/embed/${EPISODES[0].videoId}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
                style={{ border: 0 }}
              />
            </div>
            <h2
              className="mt-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
            >
              {EPISODES[0].parsha}
            </h2>
          </div>
        )}
      </motion.section>

      {/* ── new patrons since the last episode ── */}
      {newPatrons.length > 0 && (
        <motion.section {...reveal} className="mt-20" data-testid="podcast-new-patrons">
          <p className="text-[11px] uppercase" style={{ color: "#F2D589", letterSpacing: "0.24em" }}>
            New patrons this episode
          </p>
          <h2
            className="mt-4 max-w-[34ch]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
          >
            This one exists because of them.
          </h2>
          <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-testid="podcast-new-patrons-list">
            {newPatrons.map((p) => (
              <li
                key={p.id}
                className="text-lg sm:text-xl"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: p.tier === "producer" ? "#F2D589" : "#F5EFE3" }}
                data-testid={`podcast-new-patron-${p.tier}`}
              >
                {p.name}
                {p.region ? (
                  <span className="ml-2 text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.2em", fontFamily: "'Courier Prime', monospace" }}>
                    {p.region}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <Link
            to="/patrons#wall"
            className="mt-8 inline-block text-[11px] uppercase transition-colors hover:text-[#F2D589]"
            style={{ color: "#FCA07A", letterSpacing: "0.22em" }}
            data-testid="podcast-new-patrons-all"
          >
            See the full list &rarr;
          </Link>
        </motion.section>
      )}

      {/* ── archive ── */}
      <motion.section {...reveal} className="mt-24" data-testid="podcast-archive">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
            Every episode
          </h2>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a Parsha"
            data-testid="podcast-search"
            className="h-11 w-full max-w-[18rem] rounded-md px-4 text-sm outline-none"
            style={{ backgroundColor: "#241246", border: "1px solid #1A0F3C", color: "#F5EFE3" }}
          />
        </div>

        {results.length === 0 ? (
          <p className="mt-10 text-base" style={{ color: "#C6BBA9" }} data-testid="podcast-archive-empty">
            {EPISODES.length === 0
              ? "Nothing here yet — the first episode will land at the top of this list."
              : "No episode by that name yet."}
          </p>
        ) : (
          <ul className="mt-10 flex flex-col" data-testid="podcast-archive-list">
            {results.map((ep, i) => (
              <li
                key={ep.slug}
                className="py-8"
                style={{ borderTop: i === 0 ? "none" : "1px solid #241246" }}
                data-testid={`podcast-episode-${ep.number}`}
              >
                <Link to={`/podcast/${ep.slug}`} className="group block">
                  <div className="flex flex-wrap gap-8 sm:flex-nowrap">
                    {ep.art && (
                      <div
                        className="shrink-0"
                        style={{ width: 220, aspectRatio: "16 / 9", backgroundColor: "#0C0826", border: "1px solid #241246" }}
                      >
                        <img
                          src={ep.art}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                          data-testid={`podcast-episode-art-${ep.number}`}
                        />
                      </div>
                    )}
                    <div>
                      <p className="text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}>
                        Episode {ep.number} &middot; {ep.date}
                      </p>
                      <h3
                        className="mt-3 transition-colors group-hover:text-[#F2D589]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-1)" }}
                      >
                        {ep.parsha}{" "}
                        <span dir="rtl" style={{ fontFamily: "'Frank Ruhl Libre', serif", color: "#F2D589" }}>
                          {ep.parshaHe}
                        </span>
                      </h3>
                      <p className="mt-3 max-w-[70ch] text-base" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
                        {ep.blurb}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </motion.section>

      {/* ── the three asks ── */}
      <motion.section {...reveal} className="mt-24" data-testid="podcast-ctas">
        <h2
          className="max-w-[34ch]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
        >
          <SentenceLines text="Hear one. Then hear yours." />
        </h2>
        <div className="mt-10 flex flex-wrap gap-4">
          <JmlLink className={goldCta} style={goldStyle} testId="podcast-cta-tryit">
            Hear a word or verse
          </JmlLink>
          <Link to="/patrons" className={plainCta} style={plainStyle} data-testid="podcast-cta-patrons">
            Support the music
          </Link>
          <JmlLink className={plainCta} style={plainStyle} testId="podcast-cta-method">
            How it works
          </JmlLink>
        </div>
      </motion.section>
    </MarketingShell>
  );
}
